import { useEffect, useRef } from 'react';

/**
 * Hook to warn users about unsaved changes when they try to leave the page
 *
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Custom warning message (optional)
 *
 * @example
 * // In a component with editable data
 * const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
 * useUnsavedChangesWarning(hasUnsavedChanges);
 *
 * // Update hasUnsavedChanges when user edits data
 * const handleDataChange = () => {
 *   setHasUnsavedChanges(true);
 * };
 *
 * // Clear hasUnsavedChanges after save
 * const handleSave = async () => {
 *   await saveData();
 *   setHasUnsavedChanges(false);
 * };
 */
export const useUnsavedChangesWarning = (
  hasUnsavedChanges = false,
  message = 'You have unsaved changes. If you leave, your changes will be lost. Are you sure you want to leave?'
) => {
  const messageRef = useRef(message);

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    // Handle browser/tab close or refresh
    const handleBeforeUnload = (event) => {
      // Modern browsers require returnValue to be set
      event.preventDefault();
      event.returnValue = messageRef.current;
      return messageRef.current;
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
};

export default useUnsavedChangesWarning;
